import React, { useContext } from "react";
import { motion } from "framer-motion";
import StyleContext from "../../contexts/StyleContext";
import { educationInfo } from "../../portfolio";

export default function EducationModern() {
  const { isDark } = useContext(StyleContext);

  if (!educationInfo.viewEducation) {
    return null;
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    <div className="relative py-20 md:py-32 overflow-hidden" id="education">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-orange-50 to-red-50 dark:from-gray-900 dark:via-orange-900/20 dark:to-red-900/20" />

      {/* Floating Orb */}
      <motion.div
        className="absolute -right-20 top-1/4 w-96 h-96 bg-gradient-to-r from-orange-400 to-red-400 rounded-full opacity-20 blur-3xl"
        animate={{
          scale: [1, 1.2, 1],
          x: [0, -50, 0],
          y: [0, 30, 0],
        }}
        transition={{
          duration: 9,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      <div className="relative z-10 container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.div
            className="inline-block mb-4"
            whileHover={{ scale: 1.05 }}
          >
            <span className="px-4 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-full text-sm font-semibold shadow-lg">
              🎓 Academic Journey
            </span>
          </motion.div>
          <h2 className={`text-4xl md:text-5xl lg:text-6xl font-bold mb-6 ${
            isDark ? "text-white" : "text-gray-900"
          }`}>
            Education
          </h2>
          <p className={`text-lg md:text-xl max-w-2xl mx-auto ${
            isDark ? "text-gray-400" : "text-gray-600"
          }`}>
            My educational background and continuous learning journey
          </p>
        </motion.div>

        {/* Education Cards Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="max-w-6xl mx-auto space-y-8"
        >
          {educationInfo.schools.map((school, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              whileHover={{
                scale: 1.02,
                transition: { duration: 0.2 }
              }}
              className="glass backdrop-blur-xl rounded-3xl p-6 md:p-8 bg-white/60 dark:bg-gray-800/60 border border-white/20 dark:border-gray-700/20 shadow-xl hover:shadow-2xl hover:shadow-orange-500/20 transition-all duration-300"
            >
              <div className="flex flex-col md:flex-row gap-6">
                {/* Logo */}
                <div className="flex-shrink-0">
                  <div className="w-24 h-24 rounded-2xl bg-white dark:bg-gray-700 p-3 shadow-lg flex items-center justify-center">
                    <img
                      src={school.logo}
                      alt={school.schoolName}
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1">
                  {/* Header */}
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
                    <div>
                      <h3 className={`text-2xl md:text-3xl font-bold mb-2 ${
                        isDark ? "text-white" : "text-gray-900"
                      }`}>
                        {school.schoolName}
                      </h3>
                      <p className="text-lg font-semibold gradient-text mb-2">
                        {school.subHeader}
                      </p>
                      <div className="inline-block">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          isDark
                            ? "bg-orange-500/20 text-orange-300"
                            : "bg-orange-100 text-orange-700"
                        }`}>
                          📅 {school.duration}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  {school.desc && (
                    <p className={`mb-4 leading-relaxed ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}>
                      {school.desc}
                    </p>
                  )}

                  {/* Bullets */}
                  {school.descBullets && school.descBullets.length > 0 && (
                    <div className="space-y-3">
                      {school.descBullets.map((bullet, bulletIndex) => (
                        <motion.div
                          key={bulletIndex}
                          initial={{ opacity: 0, x: -10 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: bulletIndex * 0.1 }}
                          className="flex items-start gap-3"
                        >
                          <div className="flex-shrink-0 w-6 h-6 rounded-lg bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center text-white text-xs font-bold mt-0.5">
                            ✓
                          </div>
                          <p className={`text-sm leading-relaxed ${
                            isDark ? "text-gray-400" : "text-gray-600"
                          }`}>
                            {bullet}
                          </p>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom Decorative Element */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="mt-20 flex justify-center"
        >
          <div className="flex items-center gap-2">
            <div className="h-px w-20 bg-gradient-to-r from-transparent via-orange-500 to-transparent" />
            <div className="w-2 h-2 rounded-full bg-orange-500" />
            <div className="h-px w-20 bg-gradient-to-r from-transparent via-red-500 to-transparent" />
          </div>
        </motion.div>
      </div>
    </div>
  );
}
